import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  DialogContentText,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RequestInformation from './components/RequestInformation';
import PropertyInformation from './components/PropertyInformation';
import CarbonInformation from './components/CarbonInformation';
import RegistrationInfo from './components/RegistrationInfo';
import StatusSection from './components/StatusSection';
import CompensationSection from './components/CompensationSection';
import TransactionsSection from './components/TransactionsSection';
import FileUploadSection from './components/FileUploadSection';
import TestDataSection from './components/TestDataSection';
import { useFormValidation } from './hooks/useFormValidation';
import { toast } from 'react-toastify';
import { storage } from '../../../../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../../config/firebase';

const CertificationDialog = ({
  open,
  onClose,
  certification,
  setCertification,
  handleFieldChange,
  handleAddTransaction,
  handleDeleteTransaction,
  handleSave,
}) => {
  const { errors, setErrors, validateAllFields, validate } = useFormValidation(certification);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [discardCounter, setDiscardCounter] = useState(0);
  const [isDiscarding, setIsDiscarding] = useState(false);

  useEffect(() => {
    if (open) {
      setHasChanges(false);
      setSelectedFiles(null);
      setErrors({});
    }
  }, [open]);

  const handleFieldChangeAndValidate = (field, value) => {
    handleFieldChange(field, value);
    validate(field, value);
    setHasChanges(true);
  };

  const handleStatusChange = (field, value) => {
    if (field === 'status.custody') {
      handleFieldChange(field, value);
      if (value === 'CCO2 Global LLC') {
        handleAddTransaction(true, null, 'CUSTODY', 'New custody: ' + value + '.');
      }
      setHasChanges(true);
    }
  };

  const handleFileSelect = (files) => {
    setSelectedFiles(files);
    setHasChanges(true);
  };

  const handleSaveWithValidation = async () => {
    setIsLoading(true);
    try {
      const isValid = validateAllFields();
      if (!isValid) {
        toast.error('Please fix all errors before saving');
        return;
      }

      // For a new certification, check if it already exists
      if (!certification.id) {
        const certKey = certification.data?.certificationKey;
        if (certKey) {
          const docRef = doc(db, 'certifications', certKey);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            toast.error('A certification with this ID already exists');
            return;
          }
        }
      }

      // For a new certification, add the creation transaction.
      if (!certification.id) {
        handleAddTransaction(false, null, 'CREATION', 'Certification created.');
      }

      // If we have files to upload
      if (selectedFiles) {
        const certKey = certification.data?.certificationKey || certification.id;
        
        // Upload PDF
        const pdfRef = ref(storage, `certifications/${certKey}.pdf`);
        await uploadBytes(pdfRef, selectedFiles.pdf);
        
        // Upload JPG
        const jpgRef = ref(storage, `certifications/${certKey}.jpg`);
        await uploadBytes(jpgRef, selectedFiles.jpg);

        // Add transaction for file upload
        handleAddTransaction(false, null, 'UPLOAD', 'Certification files uploaded.');
      }

      // Save certification data
      await handleSave(certification);
      
      // Refresh data from database but keep dialog open
      await refreshData();
      
      // Reset file selection after successful save
      setSelectedFiles(null);
      setHasChanges(false);

      // Force refresh of FileUploadSection by incrementing the key
      setDiscardCounter(prev => prev + 1);
    } catch (error) {
      console.error('Error saving certification:', error);
      toast.error('Error saving certification');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscard = async () => {
    setIsDiscarding(true);
    try {
      if (certification?.id) {
        // For existing certification, fetch fresh data from DB
        const docRef = doc(db, 'certifications', certification.id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const freshData = docSnap.data();
          setCertification({ id: certification.id, data: freshData });
        }
      } else {
        // For new certification, reset to empty state
        setCertification({
          id: '',
          data: {
            requester: {},
            address: {},
            owners: [],
            transactions: [],
            baseYear: {},
            status: { custody: 'ABCARBON' }
          }
        });
      }

      // Reset UI states
      setSelectedFiles(null);
      setHasChanges(false);
      setErrors({});
      setDiscardCounter(prev => prev + 1);
      toast.success('Changes discarded');
    } catch (error) {
      console.error('Error discarding changes:', error);
      toast.error('Error discarding changes');
    } finally {
      setIsDiscarding(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      setConfirmCloseOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = async () => {
    try {
      await handleDiscard();
      setConfirmCloseOpen(false);
      onClose();
    } catch (error) {
      console.error('Error closing dialog:', error);
      toast.error('Error closing dialog');
    }
  };

  const refreshData = async () => {
    if (certification?.id) {
      try {
        const docRef = doc(db, 'certifications', certification.id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCertification({ id: certification.id, data: docSnap.data() });
        }
      } catch (error) {
        console.error('Error refreshing data:', error);
        toast.error('Error refreshing data');
      }
    } else {
      setCertification({
        id: '',
        data: {
          requester: {},
          address: {},
          owners: [],
          transactions: [],
          baseYear: {},
          status: { custody: 'ABCARBON' }
        }
      });
    }
    setSelectedFiles(null);
    setHasChanges(false);
    setErrors({});
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        aria-labelledby="certification-dialog-title"
      >
        <DialogTitle id="certification-dialog-title">
          {certification.id ? 'Edit Certification' : 'New Certification'}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <TestDataSection
              handleFieldChange={handleFieldChange}
              setHasChanges={setHasChanges}
              disabled={isDiscarding || isLoading}
            />
            
            <FileUploadSection 
              key={`file-upload-${discardCounter}`}
              certification={certification} 
              onFileSelect={handleFileSelect}
              handleFieldChange={handleFieldChangeAndValidate}
              disabled={isDiscarding || isLoading}
            />
            
            <PropertyInformation
              certification={certification}
              handleFieldChange={handleFieldChangeAndValidate}
              setHasChanges={setHasChanges}
              errors={errors}
              disabled={isDiscarding || isLoading}
            />

            <RequestInformation
              certification={certification}
              handleFieldChange={handleFieldChangeAndValidate}
              errors={errors}
              disabled={isDiscarding || isLoading}
            />

            <CarbonInformation
              certification={certification}
              handleFieldChange={handleFieldChangeAndValidate}
              errors={errors}
              disabled={isDiscarding || isLoading}
            />

            <RegistrationInfo
              certification={certification}
              handleFieldChange={handleFieldChangeAndValidate}
              errors={errors}
              disabled={isDiscarding || isLoading}
            />

            <StatusSection
              certification={certification}
              handleStatusChange={handleStatusChange}
              errors={errors}
              disabled={isDiscarding || isLoading}
            />

            <CompensationSection
              certification={certification}
              handleFieldChange={handleFieldChangeAndValidate}
              handleAddTransaction={handleAddTransaction}
              handleDeleteTransaction={handleDeleteTransaction}
              setHasChanges={setHasChanges}
              errors={errors}
              disabled={isDiscarding || isLoading}
            />

            <TransactionsSection
              certification={certification}
              disabled={isDiscarding || isLoading}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClose}
            disabled={isDiscarding || isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDiscard}
            color="error"
            disabled={!hasChanges || isDiscarding || isLoading}
          >
            {isDiscarding ? 'Discarding...' : 'Discard'}
          </Button>
          <Button
            onClick={handleSaveWithValidation}
            variant="contained"
            color="primary"
            disabled={isLoading || isDiscarding || Object.keys(errors).length > 0}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
      >
        <DialogTitle>
          Discard Changes?
          <IconButton
            aria-label="close"
            onClick={() => setConfirmCloseOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to discard them and close?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmCloseOpen(false)}
            disabled={isDiscarding}
          >
            Continue Editing
          </Button>
          <Button
            onClick={handleConfirmClose}
            color="error"
            disabled={isDiscarding}
          >
            {isDiscarding ? 'Discarding...' : 'Discard and Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CertificationDialog; 